// apps/frontend-vario-kaufland/src/lib/category-path.ts
//
// Baut den Anzeigenamen einer Kaufland-Kategorie als Brotkrumen-Pfad Wurzel->Kategorie.
//
// `kaufland.categories.path` ist bei allen 6.087 Zeilen NULL (live geprueft 2026-08-09,
// alle sieben Storefronts) -- gefuellt sind nur `name` (Slug, storefrontuebergreifend
// gleich) und `title_singular` (uebersetzter Anzeigename, je Storefront verschieden).
// Der Pfad entsteht deshalb hier selbst ueber die parent_id-Kette.
//
// Gemeinsam genutzt von der Einstellungen-Karte "Kaufland-Kategorien" (Task 7) und dem
// Lister-Slot (Task 8), damit der Haendler in beiden Ansichten denselben Text liest --
// nicht kopieren, sondern von hier importieren.

import type { SupabaseClient } from '@supabase/supabase-js';

/** Eintrag im lokal gehaltenen Kategorie-Katalog: Anzeigename + Elternkategorie. */
export interface KategorieKatalogEintrag {
  name: string;
  parentId: number | null;
}

/**
 * Loest die parent_id-Kette einer Kategorie zu einem lesbaren Pfad auf.
 *
 * `gesehen` schuetzt gegen einen Ring in fehlerhaften Katalogdaten, der sonst eine
 * Endlosschleife ausloesen wuerde. Fehlt die ID im Katalog (z. B. weil noch nicht
 * geladen), faellt die Funktion auf die rohe ID als String zurueck statt zu werfen.
 */
export function kategorienPfad(
  katalog: Map<number, KategorieKatalogEintrag>,
  id: number,
): string {
  const teile: string[] = [];
  let current: number | null = id;
  const gesehen = new Set<number>();
  while (current != null && !gesehen.has(current)) {
    gesehen.add(current);
    const eintrag = katalog.get(current);
    if (!eintrag) break;
    teile.unshift(eintrag.name);
    current = eintrag.parentId;
  }
  return teile.length > 0 ? teile.join(' › ') : String(id);
}

/**
 * Laedt den kompletten Kategorie-Katalog EINES Storefronts (6.087 Zeilen, Stand
 * 2026-08-09) einmalig und bestimmt dabei die Blattkategorien: eine category_id ist
 * Blatt, wenn keine andere Zeile sie als parent_id fuehrt. Die Blatt-Erkennung ist der
 * Grund, warum der Katalog VOLLSTAENDIG geladen werden muss: Fehlen Zeilen, sehen
 * Zwischenknoten wie Blaetter aus und werden faelschlich waehlbar. Kategorie-IDs sind laut
 * Migration 0039 storefrontuebergreifend identisch, nur Name/Uebersetzung unterscheiden
 * sich -- ein Storefront reicht als Quelle fuer den Katalog.
 *
 * Gemeinsam genutzt von der Einstellungen-Karte (volle Suche + Blatt-Pruefung) und dem
 * Lister-Slot (nur Pfadaufloesung fuer die eigene, kurze Kategorieliste).
 */
export const KATEGORIE_SEITENGROESSE = 1000;

/** Eine Seitenabfrage: bekommt die Spanne, liefert die Antwort im PostgREST-Format. */
export type SeitenAbruf<T> = (
  von: number,
  bis: number,
) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>;

/**
 * Holt ALLE Zeilen einer Abfrage, seitenweise.
 *
 * Der Grund, warum das eine eigene Funktion ist und nicht dreimal abgeschrieben steht:
 * PostgREST liefert hoechstens `max_rows` Zeilen (in diesem Projekt 1000, live gelesen
 * 2026-08-09) und meldet das NICHT als Fehler. Eine ungeblaetterte Abfrage bekommt also
 * lautlos einen Ausschnitt und haelt ihn fuer das Ganze. Bei `kaufland.categories` sah das
 * aus wie "die Kategorie gibt es bei Kaufland nicht"; bei `kaufland.tenant_categories` waere
 * es schlimmer gewesen, weil `kaufland_set_tenant_categories` alle nicht uebergebenen
 * manuellen Zeilen loescht -- ein Speichern haette jede Auswahl jenseits von Platz 1000
 * kommentarlos weggeworfen.
 *
 * Der Aufrufer MUSS eine feste Sortierung setzen (`order(...)`). Ohne sie darf Postgres die
 * Zeilen zwischen zwei Abfragen anders anordnen, dann fehlen Zeilen in der einen Seite und
 * tauchen in der naechsten doppelt auf.
 */
export async function ladeAlleSeiten<T>(
  abruf: SeitenAbruf<T>,
  seitengroesse: number = KATEGORIE_SEITENGROESSE,
): Promise<T[]> {
  const alle: T[] = [];
  for (let von = 0; ; von += seitengroesse) {
    const { data, error } = await abruf(von, von + seitengroesse - 1);
    if (error) throw new Error(error.message);
    const seite = data ?? [];
    alle.push(...seite);
    // Eine kurze Seite ist das Ende. Eine volle Seite kann das Ende sein, dann liefert die
    // naechste Abfrage null Zeilen und die Schleife endet dort.
    if (seite.length < seitengroesse) break;
  }
  return alle;
}

/** Eine Zeile der Auswahl des Haendlers in `kaufland.tenant_categories`. */
export interface GewaehlteKategorie {
  categoryId: number;
  addedBy: 'manual' | 'auto';
}

/**
 * Laedt die gewaehlten Kategorien EINER Storefront, vollstaendig.
 *
 * Gemeinsam genutzt vom Kategorie-Modal in den Einstellungen und vom Lister-Slot, damit die
 * Blaetterschleife nicht zweimal danebengehen kann. Vollstaendigkeit ist hier nicht kosmetisch:
 * Das Modal schickt die geladene Auswahl beim Speichern zurueck, und
 * `kaufland_set_tenant_categories` loescht jede manuelle Zeile, die nicht dabei ist. Ein
 * abgeschnittener Ladevorgang wuerde beim naechsten Speichern also Daten vernichten, ohne dass
 * irgendwo ein Fehler erschiene.
 */
export async function ladeGewaehlteKategorien(
  supabase: SupabaseClient,
  storefront: string,
): Promise<GewaehlteKategorie[]> {
  type Row = { category_id: number; added_by: 'manual' | 'auto' };
  const rows = await ladeAlleSeiten<Row>((von, bis) =>
    supabase
      .schema('kaufland')
      .from('tenant_categories')
      .select('category_id,added_by')
      .eq('storefront', storefront)
      .order('category_id', { ascending: true })
      .range(von, bis),
  );
  return rows.map((r) => ({ categoryId: Number(r.category_id), addedBy: r.added_by }));
}

export async function ladeKategorieKatalog(
  supabase: SupabaseClient,
  storefront: string,
): Promise<{ katalog: Map<number, KategorieKatalogEintrag>; blaetter: Set<number> }> {
  type KategorieRow = { category_id: number; parent_id: number | null; name: string | null; title_singular: string | null };

  // Geblaettert, NICHT in einer Abfrage: PostgREST liefert hoechstens `max_rows` Zeilen
  // (in diesem Projekt 1000, live gelesen 2026-08-09), der DE-Katalog hat 6.087. Ohne
  // Blaettern kamen also 1.000 an, und zwar lautlos -- kein Fehler, kein Hinweis, die
  // fehlenden 84 Prozent sahen aus wie "die Kategorie gibt es bei Kaufland nicht".
  // Aufgefallen an `tischkarten` (ID 40941, Position 2900 der Standardreihenfolge).
  //
  // `order('category_id')` ist fuer das Blaettern zwingend: ohne feste Sortierung darf
  // Postgres die Zeilen zwischen zwei Abfragen anders anordnen, dann fehlen Zeilen in der
  // einen Seite und tauchen in der naechsten doppelt auf.
  const rows = await ladeAlleSeiten<KategorieRow>((von, bis) =>
    supabase
      .schema('kaufland')
      .from('categories')
      .select('category_id,parent_id,name,title_singular')
      .eq('storefront', storefront)
      .order('category_id', { ascending: true })
      .range(von, bis),
  );

  const katalog = new Map<number, KategorieKatalogEintrag>();
  const elternIds = new Set<number>();
  for (const r of rows) {
    const id = Number(r.category_id);
    katalog.set(id, {
      name: r.title_singular ?? r.name ?? String(id),
      parentId: r.parent_id != null ? Number(r.parent_id) : null,
    });
    if (r.parent_id != null) elternIds.add(Number(r.parent_id));
  }
  const blaetter = new Set<number>();
  for (const id of katalog.keys()) {
    if (!elternIds.has(id)) blaetter.add(id);
  }
  return { katalog, blaetter };
}
